<div class="container">
  <app-loading [showLoading]="isLoading"></app-loading>
  <h1 class="header">
    <div class="title">BIZTEL の連携</div>
    <button
      type="button"
      mat-icon-button
      class="close-button"
      (click)="close()"
    >
      <mat-icon>close</mat-icon>
    </button>
  </h1>
  <form class="main" (submit)="onSubmit($event)">
    @if (isEditMode) {
      <p class="description">
        現在 BIZTEL 連携は<strong>有効</strong>です。API
        トークンは安全のため再表示されません。変更する場合のみ再入力してください（空欄のまま保存すると現在のトークンを維持します）。保存時に
        BIZTEL API で接続確認を行います。
      </p>
    } @else {
      <p class="description">
        BIZTEL の FQDN と API トークンを入力して連携を開始します。保存時に
        BIZTEL API で接続確認を行います。
      </p>
    }

    <div class="form">
      <div class="field">
        <label class="label" for="biztel-fqdn">FQDN * :</label>
        <div class="input-with-error">
          <div class="value">
            <input
              id="biztel-fqdn"
              class="input"
              [class.error]="fqdn.touched && fqdn.invalid"
              placeholder="例: s00000.u.biztel.jp"
              [formControl]="fqdn"
            />
          </div>
          @if (fqdn.touched && fqdn.hasError('required')) {
            <div class="input-error">FQDN は必須です</div>
          }
        </div>
      </div>

      <div class="field">
        <label class="label" for="biztel-api-token">
          API トークン{{ isEditMode ? '' : ' *' }} :
        </label>
        <div class="input-with-error">
          @if (isEditMode) {
            <div class="input-help">
              ※API
              トークンは安全のため再表示されません。変更する場合のみ入力してください（空欄のまま保存すると現在のトークンを維持します）
            </div>
          }
          <div class="value secret-value">
            <input
              id="biztel-api-token"
              class="input"
              [type]="isApiTokenVisible ? 'text' : 'password'"
              autocomplete="new-password"
              [class.error]="apiToken.touched && apiToken.invalid"
              [placeholder]="
                isEditMode ? '変更する場合のみ入力' : 'API トークン'
              "
              [formControl]="apiToken"
            />
            <button
              type="button"
              class="secret-visibility-button"
              [attr.aria-label]="
                isApiTokenVisible ? 'API トークンを隠す' : 'API トークンを表示'
              "
              (click)="isApiTokenVisible = !isApiTokenVisible"
            >
              <mat-icon>
                {{ isApiTokenVisible ? 'visibility_off' : 'visibility' }}
              </mat-icon>
            </button>
          </div>
          @if (apiToken.touched && apiToken.hasError('required')) {
            <div class="input-error">API トークンは必須です</div>
          }
        </div>
      </div>

      <div class="field">
        <label class="label" for="biztel-skill-map">スキルマップ * :</label>
        <div class="input-with-error">
          <div class="value">
            <select
              id="biztel-skill-map"
              class="input"
              [class.error]="skillMapId.touched && skillMapId.invalid"
              [formControl]="skillMapId"
            >
              <option value="" disabled>選択してください</option>
              @for (map of skillMaps; track map.id) {
                <option [value]="map.id">{{ map.name }}</option>
              }
            </select>
          </div>
          @if (skillMapId.touched && skillMapId.hasError('required')) {
            <div class="input-error">スキルマップは必須です</div>
          }
          @if (skillMapLoadError) {
            <div class="input-error">{{ skillMapLoadError }}</div>
          }
        </div>
      </div>

      <div class="field">
        <label class="label" for="biztel-queue-id">対象キューID（着信）:</label>
        <div class="input-with-error">
          <div class="input-help">
            取り込み対象の着信キューの ID
            を追加してください。未設定の場合は全キューの着信を取り込みます。キューID
            は BIZTEL 管理画面 &gt; コールセンター &gt; 一覧画面の ID
            カラムで確認できます
          </div>
          <div class="tag-input-row">
            <input
              #queueIdInputEl
              id="biztel-queue-id"
              class="input"
              inputmode="numeric"
              placeholder="例: 10004"
              [class.error]="
                isQueueIdInputInvalid() ||
                isQueueIdInputTooLong() ||
                isQueueIdDuplicated() ||
                (isSubmitted && trimmedQueueIdInput !== '')
              "
              [formControl]="queueIdInput"
              (keydown.enter)="$event.preventDefault()"
            />
            <button
              type="button"
              rectangle
              variant="secondary"
              [disabled]="isAddQueueIdDisabled()"
              (click)="addQueueId()"
            >
              追加
            </button>
          </div>
          @if (queueIds.length > 0) {
            <div class="tag-list">
              @for (queueId of queueIds; track queueId) {
                <span class="tag-chip">
                  {{ queueId }}
                  <button
                    type="button"
                    class="tag-remove"
                    [attr.aria-label]="queueId + 'を削除'"
                    (click)="removeQueueId(queueId)"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </span>
              }
            </div>
          }
          @if (isQueueIdInputInvalid()) {
            <div class="input-error">キューID は数字のみで入力してください</div>
          }
          @if (isQueueIdInputTooLong()) {
            <div class="input-error">
              キューID は 20 桁以内で入力してください
            </div>
          }
          @if (isQueueIdDuplicated()) {
            <div class="input-error">既に追加されています</div>
          }
          @if (isSubmitted && trimmedQueueIdInput !== '') {
            <div class="input-error">
              追加されていない入力値があります。「追加」を押すか、入力欄を空にしてください
            </div>
          }
          @if (isFilterListTotalAtLimit()) {
            <div class="input-help">
              対象キューIDと通知番号は合計 30 件まで設定できます
            </div>
          }
        </div>
      </div>

      <div class="field">
        <label class="label" for="biztel-outbound-caller-number">
          対象通知番号（発信）:
        </label>
        <div class="input-with-error">
          <div class="input-help">
            取り込み対象の発信の通知番号（自社側番号）を追加してください。未設定の場合は全発信を取り込みます
          </div>
          <div class="tag-input-row">
            <input
              #outboundCallerNumberInputEl
              id="biztel-outbound-caller-number"
              class="input"
              inputmode="numeric"
              placeholder="例: 05011112222"
              [class.error]="
                isOutboundCallerNumberInputInvalid() ||
                isOutboundCallerNumberInputTooLong() ||
                isOutboundCallerNumberDuplicated() ||
                (isSubmitted && trimmedOutboundCallerNumberInput !== '')
              "
              [formControl]="outboundCallerNumberInput"
              (keydown.enter)="$event.preventDefault()"
            />
            <button
              type="button"
              rectangle
              variant="secondary"
              [disabled]="isAddOutboundCallerNumberDisabled()"
              (click)="addOutboundCallerNumber()"
            >
              追加
            </button>
          </div>
          @if (outboundCallerNumbers.length > 0) {
            <div class="tag-list">
              @for (number of outboundCallerNumbers; track number) {
                <span class="tag-chip">
                  {{ number }}
                  <button
                    type="button"
                    class="tag-remove"
                    [attr.aria-label]="number + 'を削除'"
                    (click)="removeOutboundCallerNumber(number)"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </span>
              }
            </div>
          }
          @if (isOutboundCallerNumberInputInvalid()) {
            <div class="input-error">
              通知番号は数字のみ（ハイフンなし）で入力してください
            </div>
          }
          @if (isOutboundCallerNumberInputTooLong()) {
            <div class="input-error">
              通知番号は 20 桁以内で入力してください
            </div>
          }
          @if (isOutboundCallerNumberDuplicated()) {
            <div class="input-error">既に追加されています</div>
          }
          @if (isSubmitted && trimmedOutboundCallerNumberInput !== '') {
            <div class="input-error">
              追加されていない入力値があります。「追加」を押すか、入力欄を空にしてください
            </div>
          }
          @if (isFilterListTotalAtLimit()) {
            <div class="input-help">
              対象キューIDと通知番号は合計 30 件まで設定できます
            </div>
          }
        </div>
      </div>

      <div class="field">
        <label class="label" for="biztel-excluded-external-number">
          除外する相手電話番号:
        </label>
        <div class="input-with-error">
          <div class="input-help">
            取り込みから<strong>除外</strong>する<strong>通話相手（顧客側）</strong>の電話番号を追加してください。着信・発信の両方に完全一致で適用されます。数字のみ（ハイフンなし）で入力してください。※上の「対象通知番号（発信）」は自社側の番号です。取り違えないようご注意ください。除外は保存後の新しい取り込みから適用され、過去に取り込み済みの通話には影響しません。また、いちど除外された取り込み分は、後から番号を外しても再取得されません（解除後の設定を読み込む取り込みでは、除外設定中に発生した未処理の通話も取り込まれます）
          </div>
          <div class="tag-input-row">
            <input
              #excludedExternalNumberInputEl
              id="biztel-excluded-external-number"
              class="input"
              inputmode="numeric"
              placeholder="例: 0312345678"
              [class.error]="
                isExcludedExternalNumberInputInvalid() ||
                isExcludedExternalNumberInputTooLong() ||
                isExcludedExternalNumberDuplicated() ||
                (isSubmitted && trimmedExcludedExternalNumberInput !== '')
              "
              [formControl]="excludedExternalNumberInput"
              (keydown.enter)="$event.preventDefault()"
            />
            <button
              type="button"
              rectangle
              variant="secondary"
              [disabled]="isAddExcludedExternalNumberDisabled()"
              (click)="addExcludedExternalNumber()"
            >
              追加
            </button>
          </div>
          @if (excludedExternalNumbers.length > 0) {
            <div class="tag-list">
              @for (number of excludedExternalNumbers; track number) {
                <span class="tag-chip">
                  {{ number }}
                  <button
                    type="button"
                    class="tag-remove"
                    [attr.aria-label]="number + 'を削除'"
                    (click)="removeExcludedExternalNumber(number)"
                  >
                    <mat-icon>close</mat-icon>
                  </button>
                </span>
              }
            </div>
          }
          @if (isExcludedExternalNumberInputInvalid()) {
            <div class="input-error">
              電話番号は数字のみ（ハイフンなし）で入力してください
            </div>
          }
          @if (isExcludedExternalNumberInputTooLong()) {
            <div class="input-error">
              電話番号は 20 桁以内で入力してください
            </div>
          }
          @if (isExcludedExternalNumberDuplicated()) {
            <div class="input-error">既に追加されています</div>
          }
          @if (isSubmitted && trimmedExcludedExternalNumberInput !== '') {
            <div class="input-error">
              追加されていない入力値があります。「追加」を押すか、入力欄を空にしてください
            </div>
          }
          @if (isExcludedExternalNumberAtLimit()) {
            <div class="input-help">
              除外する相手電話番号は 100 件まで設定できます
            </div>
          }
        </div>
      </div>
    </div>

    <div class="footer">
      <button type="button" rectangle variant="secondary" (click)="close()">
        キャンセル
      </button>
      <button
        type="submit"
        rectangle
        variant="primary"
        [disabled]="isLoading || formGroup.invalid"
      >
        保存
      </button>
    </div>
  </form>
</div>
